import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  DailyErrorRecord,
  DailyRejectionRecord,
  DailyStylistRecord,
} from '../../types/styling-analysis.js';

/** @graph-connects none */
type MetricTab = 'count' | 'avgMinutes' | 'rejections' | 'errors';

interface DailyStylistTableProps {
  records: DailyStylistRecord[];
  rejections: DailyRejectionRecord[];
  errors: DailyErrorRecord[];
  onCellClick?: (date: string, stylistId: string, type: 'completed' | 'rejected' | 'error') => void;
}

/** @graph-connects none */
export interface StylistColumn {
  id: string;
  name: string;
}

/** @graph-connects none */
export interface PivotRow {
  date: string;
  byStylist: Map<
    string,
    {
      completedCount: number;
      avgSelectionMinutes: number;
      rejectionCount: number;
      errorCount: number;
    }
  >;
  totalCompleted: number;
  totalAvgMinutes: number;
  totalRejections: number;
  totalErrors: number;
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business フラットレコードからピボットテーブルを構築する。行=日付(降順)、列=スタイリスト+合計、完了/被キャンセル/エラー通知を集計。
 * @graph-connects none
 */
export function buildPivot(
  records: DailyStylistRecord[],
  rejections: DailyRejectionRecord[],
  errors: DailyErrorRecord[],
): {
  stylists: StylistColumn[];
  rows: PivotRow[];
} {
  const stylistMap = new Map<string, string>();
  const dateMap = new Map<
    string,
    Map<
      string,
      {
        completedCount: number;
        avgSelectionMinutes: number;
        rejectionCount: number;
        errorCount: number;
      }
    >
  >();

  const ensureDay = (date: string) => {
    let dayMap = dateMap.get(date);
    if (!dayMap) {
      dayMap = new Map();
      dateMap.set(date, dayMap);
    }
    return dayMap;
  };

  const ensureCell = (
    dayMap: Map<
      string,
      {
        completedCount: number;
        avgSelectionMinutes: number;
        rejectionCount: number;
        errorCount: number;
      }
    >,
    stylistId: string,
  ) => {
    let cell = dayMap.get(stylistId);
    if (!cell) {
      cell = { completedCount: 0, avgSelectionMinutes: 0, rejectionCount: 0, errorCount: 0 };
      dayMap.set(stylistId, cell);
    }
    return cell;
  };

  for (const r of records) {
    if (!stylistMap.has(r.stylistId)) stylistMap.set(r.stylistId, r.stylistName);
    const cell = ensureCell(ensureDay(r.date), r.stylistId);
    cell.completedCount = r.completedCount;
    cell.avgSelectionMinutes = r.avgSelectionMinutes;
  }

  for (const r of rejections) {
    if (!stylistMap.has(r.stylistId)) stylistMap.set(r.stylistId, r.stylistName);
    const cell = ensureCell(ensureDay(r.date), r.stylistId);
    cell.rejectionCount = r.rejectionCount;
  }

  for (const r of errors) {
    if (!stylistMap.has(r.stylistId)) stylistMap.set(r.stylistId, r.stylistName);
    const cell = ensureCell(ensureDay(r.date), r.stylistId);
    cell.errorCount = r.errorCount;
  }

  // 同名のスタイリストが実在する（例: NAOKO が accountId 23 と 32）。列は accountId で分かれているのに
  // ヘッダ・CSVカラム名・モーダル見出しが完全に重複して見分けられないため、同名が2人以上いるときだけ
  // accountId を添える。全員に付けると縦書きヘッダが 20 列以上並ぶこの表では可読性が大きく落ちる。
  const idsByName = new Map<string, string[]>();
  for (const [id, name] of stylistMap) {
    const ids = idsByName.get(name);
    if (ids) ids.push(id);
    else idsByName.set(name, [id]);
  }

  const stylists = [...stylistMap.entries()]
    .map(([id, name]) => ({
      id,
      name: (idsByName.get(name)?.length ?? 0) > 1 ? `${name}(${id})` : name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ja'));

  const rows: PivotRow[] = [...dateMap.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1))
    .map(([date, byStylist]) => {
      let totalCompleted = 0;
      let totalWeightedMinutes = 0;
      let totalRejections = 0;
      let totalErrors = 0;
      for (const val of byStylist.values()) {
        totalCompleted += val.completedCount;
        totalWeightedMinutes += val.avgSelectionMinutes * val.completedCount;
        totalRejections += val.rejectionCount;
        totalErrors += val.errorCount;
      }
      return {
        date,
        byStylist,
        totalCompleted,
        totalAvgMinutes:
          totalCompleted > 0 ? Math.round((totalWeightedMinutes / totalCompleted) * 10) / 10 : 0,
        totalRejections,
        totalErrors,
      };
    });

  return { stylists, rows };
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business スタイリスト別実績（完了/被キャンセル/エラー通知）の CSV エクスポート文字列を生成する。
 * @graph-connects none
 */
export function buildStylingCsv(stylists: StylistColumn[], rows: PivotRow[]): string {
  const BOM = '\uFEFF';

  const escapeCsvField = (value: string): string => {
    const sanitized = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
    if (/[",\n\r]/.test(sanitized)) {
      return `"${sanitized.replace(/"/g, '""')}"`;
    }
    return sanitized;
  };

  const headers = ['日付'];
  for (const s of stylists) {
    headers.push(
      escapeCsvField(`${s.name}_選定数`),
      escapeCsvField(`${s.name}_平均(分)`),
      escapeCsvField(`${s.name}_被キャンセル`),
      escapeCsvField(`${s.name}_エラー通知`),
    );
  }
  headers.push('合計_選定数', '合計_平均(分)', '合計_被キャンセル', '合計_エラー通知');

  const csvRows = rows.map((row) => {
    const cells: string[] = [row.date];
    for (const s of stylists) {
      const cell = row.byStylist.get(s.id);
      cells.push(
        String(cell?.completedCount ?? 0),
        String(cell?.avgSelectionMinutes ?? 0),
        String(cell?.rejectionCount ?? 0),
        String(cell?.errorCount ?? 0),
      );
    }
    cells.push(
      String(row.totalCompleted),
      String(row.totalAvgMinutes),
      String(row.totalRejections),
      String(row.totalErrors),
    );
    return cells.map(escapeCsvField).join(',');
  });

  return `${BOM}${headers.join(',')}\r\n${csvRows.join('\r\n')}`;
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling
 * @graph-business 選定数/平均時間/被キャンセル/エラー通知のタブ切替ピボットテーブル。セルクリックでドリルダウン。日付列固定。
 * @graph-connects none
 */
export function DailyStylistTable({
  records,
  rejections,
  errors,
  onCellClick,
}: DailyStylistTableProps) {
  const { stylists, rows } = useMemo(
    () => buildPivot(records, rejections, errors),
    [records, rejections, errors],
  );
  const { t } = useTranslation();
  const [tab, setTab] = useState<MetricTab>('count');

  if (records.length === 0 && rejections.length === 0 && errors.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-[var(--color-text-muted)]">
        {t('common.noData')}
      </div>
    );
  }

  const tabConfig: Record<
    MetricTab,
    { label: string; type: 'completed' | 'rejected' | 'error' | null }
  > = {
    count: { label: t('styling.metricCount'), type: 'completed' },
    avgMinutes: { label: t('styling.metricAvgMinutes'), type: null },
    rejections: { label: t('styling.metricRejections'), type: 'rejected' },
    errors: { label: t('styling.metricErrors'), type: 'error' },
  };

  const currentType = tabConfig[tab].type;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 px-4 pt-4" role="tablist" aria-label={t('styling.metricSwitch')}>
        {(Object.entries(tabConfig) as [MetricTab, { label: string }][]).map(([key, { label }]) => (
          <TabButton
            key={key}
            label={label}
            selected={tab === key}
            onClick={() => {
              setTab(key);
            }}
          />
        ))}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-20 bg-[var(--color-bg-elevated)]">
            <tr className="border-b border-[var(--color-border)]">
              <th className="sticky left-0 z-30 bg-[var(--color-bg-elevated)] px-3 py-2 text-left font-medium text-[var(--color-text-secondary)]">
                {t('common.date')}
              </th>
              <th className="px-3 py-2 text-center font-semibold text-[var(--color-text-primary)]">
                {t('styling.total')}
              </th>
              {stylists.map((s) => (
                <th
                  key={s.id}
                  className="px-3 py-2 text-center font-medium text-[var(--color-text-secondary)]"
                >
                  {s.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.date}
                className="border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-bg-elevated)]"
              >
                <td className="sticky left-0 z-10 bg-[var(--color-bg-base)] px-3 py-2 font-mono text-xs text-[var(--color-text-primary)]">
                  {row.date}
                </td>
                <MetricCell
                  row={row}
                  stylistId=""
                  tab={tab}
                  bold
                  onCellClick={
                    currentType && onCellClick
                      ? () => {
                          onCellClick(row.date, '', currentType);
                        }
                      : undefined
                  }
                />
                {stylists.map((s) => (
                  <MetricCell
                    key={s.id}
                    row={row}
                    stylistId={s.id}
                    tab={tab}
                    onCellClick={
                      currentType && onCellClick
                        ? () => {
                            onCellClick(row.date, s.id, currentType);
                          }
                        : undefined
                    }
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @graph-connects none */
function TabButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
        selected
          ? 'bg-[var(--color-accent-primary)] text-white'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-base)]'
      }`}
    >
      {label}
    </button>
  );
}

/** @graph-connects none */
function MetricCell({
  row,
  stylistId,
  tab,
  bold,
  onCellClick,
}: {
  row: PivotRow;
  stylistId: string;
  tab: MetricTab;
  bold?: boolean;
  onCellClick?: () => void;
}) {
  const base = bold ? 'font-semibold' : '';
  const empty = <span className="text-[var(--color-text-muted)]">-</span>;

  const cell = stylistId ? row.byStylist.get(stylistId) : null;
  const completed = stylistId ? (cell?.completedCount ?? 0) : row.totalCompleted;
  const avgMinutes = stylistId ? (cell?.avgSelectionMinutes ?? 0) : row.totalAvgMinutes;
  const rejectionCount = stylistId ? (cell?.rejectionCount ?? 0) : row.totalRejections;
  const errorCount = stylistId ? (cell?.errorCount ?? 0) : row.totalErrors;

  let value: number;
  let displayStr: string;
  let colorClass = '';

  switch (tab) {
    case 'count':
      value = completed;
      displayStr = String(completed);
      break;
    case 'avgMinutes':
      value = completed;
      displayStr = completed > 0 ? avgMinutes.toFixed(1) : '';
      break;
    case 'rejections':
      value = rejectionCount;
      displayStr = String(rejectionCount);
      colorClass = rejectionCount > 0 ? 'text-[var(--color-warning)]' : '';
      break;
    case 'errors':
      value = errorCount;
      displayStr = String(errorCount);
      colorClass = errorCount > 0 ? 'text-[var(--color-error)]' : '';
      break;
  }

  if (value === 0) {
    return <td className={`px-3 py-2 text-center tabular-nums ${base}`}>{empty}</td>;
  }

  if (onCellClick && tab !== 'avgMinutes') {
    return (
      <td className={`px-3 py-2 text-center tabular-nums ${base}`}>
        <button
          type="button"
          className={`cursor-pointer underline decoration-dotted underline-offset-2 hover:opacity-70 ${colorClass}`}
          onClick={onCellClick}
        >
          {displayStr}
        </button>
      </td>
    );
  }

  return (
    <td className={`px-3 py-2 text-center tabular-nums ${base} ${colorClass}`}>{displayStr}</td>
  );
}
